import React from "react";
import { Player, PlayerRole } from "../../context/GameContext";
import { useNavigate } from "react-router-dom";

interface GameOverModalProps {
  winner: string;
  players: Player[];
  myRole: PlayerRole;
  onClose: () => void;
}

const GameOverModal: React.FC<GameOverModalProps> = ({
  winner,
  players,
  myRole,
  onClose,
}) => {
  const navigate = useNavigate();

  // Determine if the current player won
  const didIWin =
    (winner === "mafia" && myRole === PlayerRole.MAFIA) ||
    (winner === "villagers" && myRole !== PlayerRole.MAFIA);

  // Group players by role
  const mafiaPlayers = players.filter((p) => p.role === PlayerRole.MAFIA);
  const villagers = players.filter((p) => p.role === PlayerRole.VILLAGER);
  const doctor = players.find((p) => p.role === PlayerRole.DOCTOR);
  const detective = players.find((p) => p.role === PlayerRole.DETECTIVE);

  const handleReturnToHome = () => {
    // Navigate directly instead of using onClose
    navigate("/");
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center p-4 z-50">
      <div className="bg-slate-800 rounded-lg max-w-2xl w-full p-6 shadow-xl">
        <div className="text-center mb-6">
          <h2 className="text-2xl font-bold mb-2">Game Over</h2>
          <div
            className={`text-xl ${didIWin ? "text-green-400" : "text-red-400"}`}
          >
            {didIWin ? "You Win!" : "You Lose!"}
          </div>
          <div className="mt-4">
            <div className="text-lg font-medium">
              {winner === "mafia" ? "The Mafia" : "The Villagers"} won the game!
            </div>
            <p className="text-slate-400 text-sm mt-1">
              {winner === "mafia"
                ? "The Mafia successfully eliminated enough villagers."
                : "The Villagers successfully eliminated all mafia members."}
            </p>
          </div>
        </div>

        <div className="mb-6">
          <h3 className="text-lg font-semibold mb-3">Player Roles</h3>

          <div className="space-y-4">
            <div className="bg-red-900/30 p-3 rounded-md">
              <h4 className="font-medium text-red-400 mb-1">Mafia</h4>
              <div className="grid grid-cols-2 gap-2">
                {mafiaPlayers.map((player) => (
                  <div key={player.id} className="text-sm">
                    {player.name}
                    {!player.isAlive && (
                      <span className="text-red-500 ml-1">☠️</span>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-green-900/30 p-3 rounded-md">
              <h4 className="font-medium text-green-400 mb-1">Doctor</h4>
              <div className="text-sm">
                {doctor ? (
                  <>
                    {doctor.name}
                    {!doctor.isAlive && (
                      <span className="text-red-500 ml-1">☠️</span>
                    )}
                  </>
                ) : (
                  "No Doctor"
                )}
              </div>
            </div>

            <div className="bg-blue-900/30 p-3 rounded-md">
              <h4 className="font-medium text-blue-400 mb-1">Detective</h4>
              <div className="text-sm">
                {detective ? (
                  <>
                    {detective.name}
                    {!detective.isAlive && (
                      <span className="text-red-500 ml-1">☠️</span>
                    )}
                  </>
                ) : (
                  "No Detective"
                )}
              </div>
            </div>

            <div className="bg-yellow-900/30 p-3 rounded-md">
              <h4 className="font-medium text-yellow-400 mb-1">Villagers</h4>
              <div className="grid grid-cols-2 gap-2">
                {villagers.map((player) => (
                  <div key={player.id} className="text-sm">
                    {player.name}
                    {!player.isAlive && (
                      <span className="text-red-500 ml-1">☠️</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="text-center">
          <button onClick={handleReturnToHome} className="btn btn-primary">
            Return to Home
          </button>
        </div>
      </div>
    </div>
  );
};

export default GameOverModal;
